import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '~/components/Button';
import { cn } from '~/lib/cn.ts';
import { useRowsHeights } from '~settings/components/Scrollable/useRowsHeights';

interface Props {
  children: React.ReactNode;
  visibleRows?: number;
  maxVisibleRows?: number;
}

function ExpandableRows({
  children,
  visibleRows = 2,
  maxVisibleRows = 10, // after this amount it starts to scroll when expanded
}: Props) {
  const { t } = useTranslation('settings');
  const [expanded, setExpanded] = useState(false);
  const containerRef = useRef<HTMLUListElement>(null);

  const { visibleRowsHeight, maxVisibleRowsHeight, isOverflow } =
    useRowsHeights(containerRef, visibleRows, maxVisibleRows);

  useEffect(() => {
    if (!isOverflow) {
      setExpanded(false);
    }
  }, [isOverflow]);

  const maxVisibleHeight = `${expanded ? maxVisibleRowsHeight : visibleRowsHeight}px`;

  return (
    <div>
      <div
        data-testid="scrollable-container"
        className={cn('overflow-hidden', {
          'rounded-xs px-md py-sm pr-0 outline outline-1 outline-offset-[-1px] bg-emphasis-default outline-emphasis-subtle':
            expanded,
        })}
      >
        <ul
          className={cn(
            'flex flex-wrap gap-sm p-px',
            expanded ? 'overflow-scroll pr-md' : 'overflow-hidden',
          )}
          ref={containerRef}
          style={{
            maxHeight: maxVisibleHeight,
          }}
        >
          {children}
        </ul>
      </div>
      {isOverflow ? (
        <Button
          className="mt-sm cursor-pointer text-body-sm text-default"
          onClick={() => setExpanded((prev) => !prev)}
          variant="tertiary"
          type="button"
        >
          {expanded ? t('showLess') : t('showMore')}
        </Button>
      ) : null}
    </div>
  );
}

ExpandableRows.displayName = 'ExpandableScrollableRows';
export { ExpandableRows };
